import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Star } from 'lucide-react';
import { cn } from '../../utils/cn';

interface StarRatingProps {
  rating: number | null;
  onChange: (rating: number | null) => void;
  maxRating?: number;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  showLabel?: boolean;
  allowClear?: boolean;
}

const SIZE_CLASSES = {
  sm: 'w-5 h-5',
  md: 'w-6 h-6',
  lg: 'w-8 h-8',
};

const RATING_LABELS: Record<number, string> = {
  1: 'Poor',
  2: 'Fair',
  3: 'Good',
  4: 'Very Good',
  5: 'Excellent',
};

export const StarRating: React.FC<StarRatingProps> = ({
  rating,
  onChange,
  maxRating = 5,
  size = 'md',
  disabled = false,
  showLabel = false,
  allowClear = false,
}) => {
  const [hoveredRating, setHoveredRating] = useState<number | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  const stars = Array.from({ length: maxRating }, (_, i) => i + 1);

  const handleStarClick = useCallback((value: number) => {
    if (disabled) return;

    // If clicking the same star and clearable, clear rating
    if (allowClear && value === rating) {
      onChange(null);
    } else {
      onChange(value);
    }
  }, [disabled, allowClear, rating, onChange]);

  const handleStarHover = useCallback((value: number | null) => {
    if (disabled) return;
    setHoveredRating(value);
  }, [disabled]);

  const getStarColor = useCallback((starValue: number): string => {
    const activeRating = hoveredRating ?? rating;
    if (activeRating && starValue <= activeRating) {
      return 'text-yellow-400 fill-yellow-400';
    }
    return 'text-gray-300 dark:text-gray-600';
  }, [hoveredRating, rating]);

  const getStarIcon = useCallback((starValue: number): 'filled' | 'outline' => {
    const activeRating = hoveredRating ?? rating;
    if (activeRating && starValue <= activeRating) {
      return 'filled';
    }
    return 'outline';
  }, [hoveredRating, rating]);

  const getLabelText = useCallback((): string => {
    const activeRating = hoveredRating ?? rating;
    if (activeRating && activeRating >= 1 && activeRating <= maxRating) {
      return RATING_LABELS[activeRating] || '';
    }
    return 'Rate your experience';
  }, [hoveredRating, rating, maxRating]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (disabled) return;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => {
          const next = prev < maxRating - 1 ? prev + 1 : prev;
          onChange(next + 1);
          return next;
        });
        break;

      case 'ArrowLeft':
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) => {
          const next = prev > 0 ? prev - 1 : prev;
          onChange(next + 1);
          return next;
        });
        break;

      case 'Home':
        e.preventDefault();
        setFocusedIndex(0);
        onChange(1);
        break;

      case 'End':
        e.preventDefault();
        setFocusedIndex(maxRating - 1);
        onChange(maxRating);
        break;

      case 'Escape':
        e.preventDefault();
        if (allowClear) {
          onChange(null);
          setFocusedIndex(-1);
        }
        break;

      case ' ':
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0) {
          handleStarClick(focusedIndex + 1);
        }
        break;
    }
  }, [disabled, maxRating, onChange, allowClear, focusedIndex, handleStarClick]);

  // Update focused index when rating changes externally
  useEffect(() => {
    if (rating !== null && rating >= 1 && rating <= maxRating) {
      setFocusedIndex(rating - 1);
    } else {
      setFocusedIndex(-1);
    }
  }, [rating, maxRating]);

  return (
    <div className="space-y-2">
      {/* Star Container */}
      <div
        ref={containerRef}
        role="radiogroup"
        aria-label="Rate your experience from 1 to 5 stars"
        aria-required="false"
        className={cn(
          'flex items-center gap-1',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        onMouseLeave={() => handleStarHover(null)}
      >
        {stars.map((starValue, index) => {
          const isSelected = rating === starValue;
          const isFocused = focusedIndex === index;
          const isFilled = getStarIcon(starValue) === 'filled';

          return (
            <button
              key={starValue}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={`${starValue} star${starValue !== 1 ? 's' : ''}`}
              disabled={disabled}
              tabIndex={-1} // Container handles keyboard focus
              onClick={() => handleStarClick(starValue)}
              onMouseEnter={() => handleStarHover(starValue)}
              className={cn(
                'cursor-pointer transition-all duration-150 ease-in-out',
                'hover:scale-110 active:scale-95',
                'focus:outline-none',
                isFocused && 'ring-2 ring-primary ring-offset-2 rounded',
                disabled && 'cursor-not-allowed hover:scale-100 active:scale-100',
                'mobile-touch-target' // Minimum 48x48px touch target
              )}
            >
              <Star
                className={cn(
                  SIZE_CLASSES[size],
                  'transition-colors duration-150',
                  getStarColor(starValue),
                  isFilled && 'fill-current'
                )}
                strokeWidth={1.5}
              />
            </button>
          );
        })}
      </div>

      {/* Label Text */}
      {showLabel && (
        <p
          className={cn(
            'text-sm font-medium transition-colors duration-150',
            hoveredRating || rating
              ? 'text-foreground'
              : 'text-muted-foreground'
          )}
          aria-live="polite"
          aria-atomic="true"
        >
          {getLabelText()}
        </p>
      )}
    </div>
  );
};

export default StarRating;
